import {
  activitiesLeft,
  plannedOf,
  countryName,
  formatMoney,
  formatRange,
  live,
  AlertIcon,
  BranchIcon,
  BedIcon,
  BinIcon,
  DayPlanIcon,
  MoreIcon,
  Popover,
  FoodIcon,
  PencilIcon,
  TicketIcon,
  TicketPlusIcon,
  type Mode,
  type Money,
  type MoneyRange,
  type Plan,
  type Hotel,
  type PlanStop,
  type RangeInput,
  type Variant,
  type Booking,
  type Spot,
  type TripDocument,
} from '@trippath/shared';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';

import { stayChange, ticketChange, wayInChange, wayOnChange } from './costChanges';
import { daysAfter } from '../../lib/clock';
import { cameInto, wayClock, type WayClock } from '../../lib/wayClock';
import { CostEdit, type CostKey, type CostValues } from './CostEdit';
import { EndsPick } from '../place/EndsPick';
import { IncidentMark, replacedIn } from '../booking/Incident';
import { FileView } from '../file/FileView';
import { WayPick } from '../way/WayPick';
import { priceSaid } from '../../lib/format';
import { dayRange } from '../../lib/countryFacts';
import { cents } from '../../lib/stopInput';
import type { PlaceChange } from '../../lib/variantEdit';
import { ChangeMark, ModeIcon } from '../icons/ModeIcon';
import { StatusIcon } from '../icons/StatusIcon';
import { StayEdit } from '../booking/StayEdit';
import { variantName } from '../variants/Variants';
import { useWidgetBar } from '../widget/Widget';
import { StopWeather } from '../weather/StopWeather';

/** `18.10` from `2026-10-18`. */
function short(iso: string): string {
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
}

/** `18–20.10`: a stay's first night to the morning it ends. */
function span(from: string, nights: number): string {
  const end = new Date(`${from}T00:00:00Z`);
  end.setUTCDate(end.getUTCDate() + nights);
  return `${from.slice(8, 10)}–${short(end.toISOString().slice(0, 10))}`;
}

const times = (range: MoneyRange, by: number): MoneyRange => ({
  min: { ...range.min, minor: range.min.minor * by },
  max: { ...range.max, minor: range.max.minor * by },
});

/** A range shared over `days`, rounded to the cent. */
const perDay = (range: MoneyRange, days: number): MoneyRange => ({
  min: { ...range.min, minor: Math.round(range.min.minor / days) },
  max: { ...range.max, minor: Math.round(range.max.minor / days) },
});

const plus = (a: MoneyRange, b: MoneyRange): MoneyRange => ({
  min: { ...a.min, minor: a.min.minor + b.min.minor },
  max: { ...a.max, minor: a.max.minor + b.max.minor },
});

interface Props {
  plan: Plan;
  stop: PlanStop;
  /** Opens the stop to edit; a variant's place is not edited here. */
  onEdit?: () => void;
  /** The variant a new place picked is in, rather than a stop of the plan. */
  variant?: Variant;
  /** Books a ticket for the way on: its name, the link it was found at, its price and times. */
  onTicket?: (way: TicketWay) => void;
  /** Sets what the car uses and a litre's prices. */
  onFuel?: () => void;
  /** Books a stay here, in place. */
  onBook?: () => void;
  /** Opens a booking, a stay or a ticket, to change or remove it. */
  onBooking?: (booking: Booking) => void;
  /** Removes the stop: not given while a booked ticket goes to it. */
  onRemove?: (() => void) | undefined;
  /** Makes a branch of the trip that parts here: a trip of its own for some of its people. */
  onBranch?: () => void;
  /** Where its days open from inside: the city's own view, when it has days. */
  daysAt?: string | undefined;
  /** Says something happened to a booking: its time changed, it is late, or it was cancelled. */
  onIncident?: (booking: Booking) => void;
  /** Says where the way on leaves from and arrives at: a place, or `null` to work it out. */
  onEnds?: (ends: { from: Spot | null; to: Spot | null }) => void;
  /** The trip's documents with a file, its tickets' and bookings' PDFs and photos among them. */
  files?: readonly TripDocument[];
  /** Changes its days, its costs, its ways: each price pressed opens what it is made of. */
  onChange?: ((change: PlaceChange) => Promise<unknown>) | undefined;
  /** The stop whose way on leads here, shown first for a variant's place, and the ticket in
   * mind for it. */
  into?: { stop: PlanStop; ticket: Draft | null } | null | undefined;
  /** A variant's place's drafts: the hotels in mind, and the ticket in mind for its way on. */
  drafts?: { hotels: Hotel[]; ticket: Draft | null };
  /** The day of its weather shown, pressed on the timeline: brought into view in full. */
  weatherDay?: string | null;
}

/** The way on a ticket is booked for. */
export interface TicketWay {
  name: string;
  link: string | null;
  estimate: RangeInput;
  departure: string | null;
  arrival: string | null;
  /** Its arrival, about, from its departure: local at its end. */
  about: (departure: string) => string | null;
  /** The times typed, local at each end, as kept: instants. */
  kept: WayClock['kept'];
  /** By car: what is booked is a car rented, not a ticket. */
  rental?: boolean;
  /** By air: its booking number is a PNR. */
  flight?: boolean;
  /** A ticket cancelled on this way, the new one bought in its place: its id and price. */
  replaces?: { id: string; price: Money | null };
}

/** A ticket in mind: its name and where it is. */
interface Draft {
  name: string;
  link: string | null;
}

/** A way's price to change: priced at nothing yet, what such a way typically costs. */
function priceOffered(stop: PlanStop, out: NonNullable<PlanStop['out']>): MoneyRange {
  if (out.estimate.max.minor > 0) return out.estimate;
  return stop.ways.find((one) => one.mode === out.mode)?.estimate ?? out.estimate;
}

/** What the button buying a way's ticket says: a car is rented, the rest ticketed. */
export const ticketWord = (way: TicketWay) =>
  way.rental ? 'plan.addRental' : 'plan.addTicket';

/**
 * The way on from a stop of the plan a ticket is booked for: named by its towns, its link, its
 * estimate and its times; in place of one cancelled on it and not replaced, that one. None when
 * no ticket is bought here: no way on, one bought already, a change on the way (its journey's
 * start books it), or the traveller's own car.
 */
export function ticketWayOf(plan: Plan, stop: PlanStop): TicketWay | null {
  const { out } = stop;
  const bought = stop.bookings.some((b) => b.kind === 'ticket' && live(b));
  if (!out || bought || stop.transfer || (out.mode === 'car' && plan.car.own)) return null;
  const next = plan.stops[plan.stops.indexOf(stop) + 1];
  const clockOn = wayClock(stop, next ? next.zone : null, cameInto(plan, stop));
  const cancelled = stop.bookings.findLast(
    (b) =>
      b.kind === 'ticket' &&
      !live(b) &&
      !plan.stops.some((s) => s.bookings.some((n) => n.replaces === b.id)),
  );
  return {
    // A ticket is named by its towns: it says itself what it is.
    name: `${stop.city} → ${next ? next.city : '…'}`,
    link: out.link,
    estimate: cents(out.estimate),
    departure: clockOn.departure,
    arrival: clockOn.arrival,
    about: clockOn.about,
    kept: clockOn.kept,
    rental: out.mode === 'car',
    flight: out.mode === 'flight',
    ...(cancelled && {
      replaces: {
        id: cancelled.id,
        // Its money goes on the new one only when it was given back.
        price: cancelled.incident?.refund === 'received' ? cancelled.price : null,
      },
    }),
  };
}

/**
 * The stop picked on the route (the App mockup's lower panel): its bookings, the nights still
 * without a bed, what is estimated for the days, the way on, and what a day there costs.
 */
export function StopDetail({
  plan,
  stop,
  onEdit,
  variant,
  onTicket,
  onFuel,
  onBook,
  onBooking,
  onIncident,
  onBranch,
  onRemove,
  daysAt,
  onEnds,
  files = [],
  onChange,
  into,
  drafts,
  weatherDay = null,
}: Props) {
  const { t, i18n } = useTranslation();
  const [viewing, setViewing] = useState<TripDocument | null>(null);
  const [menu, setMenu] = useState(false);
  const bar = useWidgetBar();
  // How a stop of the plan goes on is picked from the ways that can go there.
  const pick =
    variant === undefined && stop.ways.length > 0 && onChange
      ? (way: { mode: Mode; estimate: MoneyRange }) => {
          void onChange({
            mode: way.mode,
            way: {
              estimate: cents(way.estimate),
              departureAt: null,
              arrivalAt: null,
              link: null,
            },
          });
        }
      : undefined;

  // A booking's file: kept with the documents at its stop, by its name, when it was attached.
  const fileOf = (booking: Booking) =>
    files.find(
      (d) =>
        d.stop === stop.id &&
        d.name === booking.name &&
        d.kind === (booking.kind === 'ticket' ? 'ticket' : 'booking') &&
        d.file !== null,
    );
  // A ticket's flight as read: `Finnair AY 100 · `.
  const flightOf = (flight: { airline: string | null; number: string } | null | undefined) => {
    if (!flight) return null;
    const number = `${flight.number.slice(0, 2)} ${flight.number.slice(2)}`;
    return `${flight.airline ? `${flight.airline} ` : ''}${number} · `;
  };
  // What can be done with a booking: see its file, change it.
  const bookingActions = (booking: Booking | undefined) => {
    if (booking === undefined) return null;
    const file = fileOf(booking);
    return (
      <>
        {file && (
          <button
            type="button"
            className="link-button row-action"
            onClick={() => {
              setViewing(file);
            }}
          >
            {t('plan.file')}
          </button>
        )}
        {onIncident && (
          <button
            type="button"
            className="icon-button icon-button-small"
            aria-label={t('incident.open', { name: booking.name })}
            title={t('incident.open', { name: booking.name })}
            onClick={() => {
              onIncident(booking);
            }}
          >
            <AlertIcon />
          </button>
        )}
        {onBooking && (
          <button
            type="button"
            className="icon-button icon-button-small"
            aria-label={t('plan.changeBooking', { name: booking.name })}
            title={t('plan.changeBooking', { name: booking.name })}
            onClick={() => {
              onBooking(booking);
            }}
          >
            <PencilIcon />
          </button>
        )}
      </>
    );
  };
  const lang = i18n.language;
  const at = plan.stops.indexOf(stop);
  // Where its way on goes: the next place of the variant, or the stop it joins the plan at.
  const next = variant
    ? (variant.added[variant.added.findIndex((s) => s.id === stop.id) + 1] ?? variant.rejoin)
    : plan.stops[at + 1];
  const money = (amount: Money) => formatMoney(amount, lang);
  const daily = plus(stop.food, stop.local);
  // The things planned with a price are known money; the activities' estimate is what they leave.
  const pricedThings = stop.agenda.filter((thing) => thing.price !== null);
  const planned = plannedOf(stop);
  const left = activitiesLeft(stop);
  const days = plus(times(daily, stop.days), left);
  // The tickets bought for the way on: once there is one, it is known money and stays as it is.
  // A ticket cancelled holds nothing: it is said among the bookings, struck through.
  const tickets = stop.bookings.filter((b) => b.kind === 'ticket' && live(b));
  // The ticket to buy for the way on, when one is bought here.
  const ticketWay = onTicket ? ticketWayOf(plan, stop) : null;
  const ticketTotal = tickets.reduce((sum, b) => sum + b.price.minor, 0);
  // What a stay's or the days' price adds to a day there; a way is spent on the day it goes.
  const share = (range: MoneyRange) =>
    stop.days > 0 ? formatRange(perDay(range, stop.days), lang) : '';
  const { out } = stop;
  const toCity = next ? next.city : '…';
  const way = out
    ? t('plan.way', {
        mode: t(`mode.${out.mode}`),
        from: stop.city,
        to: toCity,
      })
    : '';
  const currency = plan.trip.currency;
  // The stop its way on arrives at, for its time zone.
  const onward = variant
    ? (variant.added[variant.added.findIndex((s) => s.id === stop.id) + 1] ??
      plan.stops.find((s) => s.id === variant.rejoin?.id))
    : plan.stops[at + 1];
  // When a way leaves and arrives, local at each end: as said — `06:10 +1` the day it does —
  // else about when by how long it takes.
  const clock = (leg: NonNullable<PlanStop['out']>, from: PlanStop, toZone: string | null) => {
    const local = wayClock(from, toZone, cameInto(plan, from));
    if (leg.departureAt === null || local.departure === null) return '';
    const days =
      leg.arrivalAt === null ? 0 : daysAfter(leg.departureAt, leg.arrivalAt, from.zone, toZone);
    const estimate = local.about(local.departure);
    const arrives =
      local.arrival === null
        ? estimate === null
          ? '…'
          : `~${estimate}`
        : days > 0
          ? `${local.arrival} +${String(days)}`
          : local.arrival;
    return ` · ${t('plan.times', { leaves: local.departure, arrives })}`;
  };
  const toZone = onward ? onward.zone : null;
  // Where its way on leaves and arrives: a late ticket's times are each local there.
  const zones = useMemo(() => ({ from: stop.zone, to: toZone }), [stop.zone, toZone]);
  // A flight's two airports by their codes, when both ends are airports.
  const codeOf = (end: { name: string } | null) => {
    const code = end ? /\(([A-Z]{3})\)$/.exec(end.name)?.[1] : undefined;
    return end && code ? { name: end.name, code } : null;
  };
  const flownFrom = out?.mode === 'flight' ? codeOf(out.from) : null;
  const flownTo = codeOf(out?.to ?? null);
  const flown =
    flownFrom && flownTo && next ? { from: flownFrom, to: flownTo, city: next.city } : null;
  const clockOn = wayClock(stop, toZone, cameInto(plan, stop));
  const [firstTicket] = tickets;
  // A change on the way is part of a journey: from the stop before it that is not one, to the
  // next such — priced and ticketed at its start.
  const journeyStart = stop.transfer
    ? plan.stops.slice(0, Math.max(at, 0)).findLast((s) => !s.transfer)
    : undefined;
  const journeyEnd = plan.stops.slice(at + 1).find((s) => !s.transfer);
  // The way's flight, and whether its ticket or journey is said after it.
  const flightSaid = out ? flightOf(firstTicket ? firstTicket.flight : out.flight) : null;
  const statusSaid = tickets.length > 0 || journeyStart !== undefined;
  // A journey's start says where it changes on the way.
  const changes = stop.transfer
    ? []
    : plan.stops
        .slice(at + 1, journeyEnd ? plan.stops.indexOf(journeyEnd) : undefined)
        .filter((s) => s.transfer);
  // A price that opens what it is made of, when it can be changed here.
  const priced = (
    shown: string,
    label: string,
    ranges: { key: CostKey; label: string; value: MoneyRange; optional?: boolean }[],
    save: (values: CostValues) => PlaceChange,
    extra: {
      times?: WayClock;
      name?: { label: string; value: string | null };
      link?: string | null;
    } = {},
  ) =>
    onChange ? (
      <CostEdit
        label={label}
        currency={currency}
        ranges={ranges.map((r) => ({ ...r, value: cents(r.value) }))}
        {...extra}
        // The times typed, local at each end, kept as instants.
        onSave={(values) =>
          onChange(
            save(extra.times ? { ...values, kept: extra.times.kept(values.times) } : values),
          )
        }
      >
        {shown}
      </CostEdit>
    ) : (
      shown
    );
  // A ticket for the way on is said on the way's own row, not again among the bookings.
  const bookings = stop.bookings
    .filter((b) => !(out && b.kind === 'ticket' && live(b)))
    .toSorted((a, b) => (a.day ?? '').localeCompare(b.day ?? ''));
  const ticketLink = tickets.find((b) => b.link !== null)?.link ?? null;
  const allPaid = tickets.every((b) => b.pay === 'paid');
  // The way's link: its ticket's, or where it was found.
  const wayLink = tickets.length > 0 ? ticketLink : out ? out.link : null;

  // On the widget's bar: its day plan by its mark, and what is done with the stop in one menu —
  // on a trip read only, its day plan alone.
  const tools = (onEdit !== undefined || daysAt !== undefined) && (
    <div className="stop-tools">
      {daysAt && stop.days > 0 && (
        <Link
          className="icon-button icon-button-small"
          to={daysAt}
          aria-label={t('city.open')}
          title={t('city.open')}
        >
          <DayPlanIcon />
        </Link>
      )}
      {onEdit && (
        <Popover
          open={menu}
          onOpenChange={setMenu}
          align="end"
          label={t('plan.stopMenu')}
          trigger={(props) => (
            <button
              type="button"
              className="icon-button icon-button-small"
              aria-label={t('plan.stopMenu')}
              title={t('plan.stopMenu')}
              {...props}
            >
              <MoreIcon />
            </button>
          )}
        >
          <div className="data-menu stop-menu" role="menu" aria-label={t('plan.stopMenu')}>
            {[
              { key: 'edit', label: t('plan.editStop'), icon: <PencilIcon />, run: onEdit },
              onBook && {
                key: 'book',
                label: t('plan.addStay'),
                icon: <BedIcon />,
                run: onBook,
              },
              onBranch && {
                key: 'branch',
                label: t('family.here'),
                icon: <BranchIcon />,
                run: onBranch,
              },
              onRemove && {
                key: 'remove',
                label: t('plan.removeStop', { city: stop.city }),
                icon: <BinIcon />,
                run: onRemove,
              },
            ]
              .filter((item) => item !== undefined)
              .map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  className={`data-menu-item${item.key === 'remove' ? ' stop-menu-danger' : ''}`}
                  onClick={() => {
                    setMenu(false);
                    item.run();
                  }}
                >
                  {item.icon}
                  {item.label}
                </button>
              ))}
          </div>
        </Popover>
      )}
    </div>
  );

  return (
    <section className="panel stop-detail" aria-labelledby="stop-detail-title">
      {tools && (bar ? createPortal(tools, bar) : tools)}
      <div className="stop-detail-head">
        <div>
          {/* Its name is on the widget's bar. */}
          <h2 id="stop-detail-title" className="visually-hidden">
            {stop.city}, {countryName(stop.country, lang)}
          </h2>
          <p className="quiet">
            {/* Its days as one range, `27.–29. okt`; a stop of no night its one day. */}
            {variant
              ? t('plan.placeOf', {
                  name: variantName(variant),
                  when: dayRange(stop.start, stop.end, lang),
                  days: t('trips.days', { count: stop.days }),
                })
              : t('plan.stopOf', {
                  when: dayRange(stop.start, stop.end, lang),
                  days: t('trips.days', { count: stop.days }),
                })}
          </p>
        </div>
        {variant && onChange && (
          <div className="stop-detail-actions">
            <CostEdit
              label={t('plan.changeDays', { city: stop.city })}
              currency={currency}
              ranges={[]}
              days={stop.days}
              onSave={(values) => onChange({ days: values.days })}
            >
              <PencilIcon size={14} />
            </CostEdit>
          </div>
        )}
      </div>

      {!variant && (
        <StopWeather
          // A day pressed on the timeline starts it afresh at that day.
          key={`${stop.id} ${weatherDay ?? ''}`}
          trip={plan.trip.id}
          stop={stop.id}
          day={weatherDay}
          reveal={weatherDay !== null}
        />
      )}

      <div
        className="stop-table"
        role="table"
        aria-label={t('plan.stopCosts', { city: stop.city })}
      >
        <div role="row" className="stop-table-head">
          <span role="columnheader">{t('plan.date')}</span>
          <span role="columnheader">{t('plan.item')}</span>
          <span role="columnheader">{t('plan.price')}</span>
          <span role="columnheader">{t('plan.perDayShort')}</span>
          <span role="columnheader">
            <span className="visually-hidden">{t('plan.status')}</span>
          </span>
        </div>
        {into?.stop.out && (
          <div role="row" className="stop-way">
            <span role="cell" className="mono">
              {short(wayClock(into.stop, stop.zone, cameInto(plan, into.stop)).day)}
            </span>
            <span role="cell">
              <span className="quiet">{t('plan.wayHere')}:</span>{' '}
              {t('plan.way', {
                mode: t(`mode.${into.stop.out.mode}`),
                from: into.stop.city,
                to: stop.city,
              })}
              {clock(into.stop.out, into.stop, stop.zone)}
              {into.ticket && <strong> · {into.ticket.name}</strong>}
              {into.stop.out.link !== null && (
                <>
                  {' '}
                  <a
                    className="text-link"
                    href={into.stop.out.link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('plan.openLink')}
                  </a>
                </>
              )}
            </span>
            <span role="cell" className="mono strong">
              {priced(
                formatRange(into.stop.out.estimate, lang),
                t('plan.changeWay', {
                  way: t('plan.way', {
                    mode: t(`mode.${into.stop.out.mode}`),
                    from: into.stop.city,
                    to: stop.city,
                  }),
                }),
                [
                  {
                    key: 'estimate',
                    label: t('stopForm.estimate'),
                    value: into.stop.out.estimate,
                  },
                ],
                wayInChange,
                {
                  times: wayClock(into.stop, stop.zone, cameInto(plan, into.stop)),
                  name: {
                    label: t('plan.ticket'),
                    value: into.ticket ? into.ticket.name : null,
                  },
                  link: into.stop.out.link,
                },
              )}
            </span>
            <span role="cell" />
            <span role="cell">
              <StatusIcon status="estimate" />
            </span>
          </div>
        )}
        {bookings.map((booking) => (
          <div
            role="row"
            key={booking.id}
            className={live(booking) ? undefined : 'stop-row-cancelled'}
          >
            <span role="cell" className="mono">
              {booking.day === null
                ? '—'
                : booking.kind === 'stay' && booking.nights > 0
                  ? span(booking.day, booking.nights)
                  : short(booking.day)}
            </span>
            <span role="cell" className="stop-way-cell">
              <span>
                {booking.link === null ? (
                  booking.name
                ) : (
                  <a href={booking.link} target="_blank" rel="noreferrer">
                    {booking.name} ↗
                  </a>
                )}
                {booking.kind === 'stay' &&
                  ` · ${t('plan.nightsShort', { count: booking.nights })}`}
                {(booking.checkIn ?? booking.checkOut) !== null &&
                  ` · ${t('plan.hours', { checkIn: booking.checkIn ?? '…', checkOut: booking.checkOut ?? '…' })}`}{' '}
                ·{' '}
                <span className={`pay pay-${booking.pay}`}>
                  {t(
                    booking.pay === 'paid'
                      ? 'plan.payPaid'
                      : booking.kind === 'ticket'
                        ? 'plan.payLaterTicket'
                        : 'plan.payLater',
                  )}
                </span>
                <IncidentMark booking={booking} replaced={replacedIn(plan, booking)} />
              </span>
              <span className="stop-way-actions">{bookingActions(booking)}</span>
            </span>
            <span role="cell" className="mono strong">
              {priceSaid(booking.price, booking.paid, lang)}
            </span>
            <span role="cell" className="mono">
              {booking.kind === 'stay' && booking.nights > 0
                ? money({
                    ...booking.price,
                    minor: Math.round(booking.price.minor / booking.nights),
                  })
                : ''}
            </span>
            <span role="cell">{live(booking) && <StatusIcon status="booked" />}</span>
          </div>
        ))}
        {stop.openNights > 0 && !stop.unresolved && (
          <div role="row" className="stop-open">
            <span role="cell" />
            <span role="cell" className="stop-icon-cell">
              <ModeIcon mode="stay" size={14} />
              {t('plan.openRow', {
                count: stop.openNights,
                range: formatRange(stop.accommodation, lang),
              })}
              {drafts?.hotels.slice(0, 1).map((hotel) => (
                <span key={hotel.name}>
                  {' · '}
                  <strong>{hotel.name}</strong>
                  {hotel.link !== null && (
                    <>
                      {' '}
                      <a
                        className="text-link"
                        href={hotel.link}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t('plan.openLink')}
                      </a>
                    </>
                  )}
                  {drafts.hotels.length > 1 && (
                    <span className="quiet">
                      {' '}
                      {t('plan.moreHotels', { count: drafts.hotels.length - 1 })}
                    </span>
                  )}
                </span>
              ))}
            </span>
            <span role="cell" className="mono">
              {drafts && onChange ? (
                <StayEdit
                  label={t('plan.changeBed', { city: stop.city })}
                  currency={currency}
                  night={cents(stop.accommodation)}
                  hotels={drafts.hotels}
                  nights={stop.openNights}
                  onSave={({ night, hotels }) => onChange({ accommodation: night, hotels })}
                >
                  {formatRange(times(stop.accommodation, stop.openNights), lang)}
                </StayEdit>
              ) : (
                priced(
                  formatRange(times(stop.accommodation, stop.openNights), lang),
                  t('plan.changeBed', { city: stop.city }),
                  [
                    {
                      key: 'accommodation',
                      label: t('stopForm.accommodation'),
                      value: stop.accommodation,
                    },
                  ],
                  stayChange,
                )
              )}
            </span>
            <span role="cell" className="mono stop-share" data-unit={t('plan.perDaySuffix')}>
              {share(times(stop.accommodation, stop.openNights))}
            </span>
            <span role="cell">
              <span
                className="status-icon status-icon-open"
                role="img"
                aria-label={t('plan.open')}
              />
            </span>
          </div>
        )}
        {pricedThings.length > 0 && (
          <div role="row" className="stop-planned">
            <span role="cell" />
            <span role="cell">{t('plan.plannedRow', { count: pricedThings.length })}</span>
            <span role="cell" className="mono strong">
              {money(planned)}
            </span>
            <span role="cell" className="mono stop-share" data-unit={t('plan.perDaySuffix')}>
              {share({ min: planned, max: planned })}
            </span>
            <span role="cell">
              <StatusIcon
                status={pricedThings.every((thing) => thing.booked) ? 'booked' : 'estimate'}
              />
            </span>
          </div>
        )}
        {!stop.transfer && (
          <div role="row" className="stop-estimates">
            <span role="cell" />
            {/* What a day costs, each by its mark, named when pointed at. */}
            <span role="cell" className="stop-estimates-cell">
              {(
                [
                  ['food', <FoodIcon key="food" size={14} />, stop.food, true],
                  ['local', <ModeIcon key="local" mode="bus" size={14} />, stop.local, true],
                  ['activities', <TicketIcon key="activities" size={14} />, left, false],
                ] as const
              ).map(([key, icon, range, daily]) => (
                <span
                  key={key}
                  className="stop-estimate"
                  title={t(`plan.estimate.${key}`)}
                  aria-label={`${t(`plan.estimate.${key}`)}: ${formatRange(range, lang)}`}
                >
                  {icon}
                  {formatRange(range, lang)}
                  {daily && t('plan.perDaySuffix')}
                </span>
              ))}
            </span>
            <span role="cell" className="mono">
              {priced(
                formatRange(days, lang),
                t('plan.changeDaily', { city: stop.city }),
                [
                  { key: 'food', label: t('stopForm.food'), value: stop.food },
                  { key: 'local', label: t('stopForm.local'), value: stop.local },
                  {
                    key: 'activities',
                    label: t('stopForm.activities'),
                    value: stop.activities,
                    optional: true,
                  },
                ],
                stayChange,
              )}
            </span>
            <span role="cell" className="mono stop-share" data-unit={t('plan.perDaySuffix')}>
              {share(days)}
            </span>
            <span role="cell">
              <StatusIcon status="estimate" />
            </span>
          </div>
        )}
        {!out && pick && next && (
          // Not decided yet: how it goes on is picked here, each way priced.
          <div role="row" className="stop-way">
            <span role="cell" className="mono">
              {short(stop.end)}
            </span>
            <span role="cell" className="stop-way-cell">
              <span>
                {stop.city} → {next.city} · <span className="quiet">{t('plan.wayOpen')}</span>
              </span>
              <span className="stop-way-actions">
                <WayPick label={t('plan.howOn')} ways={stop.ways} mode={null} onPick={pick} />
              </span>
            </span>
            <span role="cell" />
            <span role="cell" />
            <span role="cell" />
          </div>
        )}
        {out && (
          <div role="row" className="stop-way">
            <span role="cell" className="mono">
              {short(clockOn.day)}
            </span>
            <span role="cell" className="stop-way-cell">
              <span>
                {flown ? (
                  // A flight between airports by their codes, each named when pointed at.
                  <>
                    {t('mode.flight')} {stop.city}{' '}
                    <abbr title={flown.from.name}>{flown.from.code}</abbr> → {flown.city}{' '}
                    <abbr title={flown.to.name}>{flown.to.code}</abbr>
                  </>
                ) : (
                  way
                )}
                {clock(out, stop, toZone)}
                {changes.length > 0 && journeyEnd && (
                  <span className="quiet">
                    {' · '}
                    <ChangeMark />{' '}
                    {t('plan.changeVia', {
                      via: changes.map((s) => s.city).join(', '),
                      to: journeyEnd.city,
                    })}
                  </span>
                )}
                {drafts?.ticket && <strong> · {drafts.ticket.name}</strong>}
                {/* The flight booked: its airline and number, as the boarding pass says. */}
                {/* Before a ticket, the flight it is planned on. No ticket and no journey:
                    its status mark says it is an estimate, in no words. */}
                {(flightSaid !== null || statusSaid) && ' · '}
                {statusSaid ? flightSaid : flightSaid?.replace(/ · $/, '')}
                {tickets.length > 0 ? (
                  <>
                    <span className={allPaid ? 'pay pay-paid' : 'pay pay-later'}>
                      {t(allPaid ? 'plan.payPaid' : 'plan.payLaterTicket')}
                    </span>
                    {/* Its time changed or late; or bought in place of one cancelled. */}
                    {tickets.map((ticket) => (
                      <span key={ticket.id}>
                        {ticket.incident !== null && ' ·'}
                        <IncidentMark booking={ticket} replaced={false} zones={zones} />
                        {ticket.replaces !== null && (
                          <span className="quiet"> · {t('incident.replacement')}</span>
                        )}
                      </span>
                    ))}
                  </>
                ) : journeyStart ? (
                  <span className="quiet">
                    {t('plan.inJourney', {
                      from: journeyStart.city,
                      to: journeyEnd ? journeyEnd.city : toCity,
                    })}
                  </span>
                ) : null}
                {wayLink !== null && (
                  <>
                    {' · '}
                    <a className="text-link" href={wayLink} target="_blank" rel="noreferrer">
                      {t(tickets.length > 0 ? 'plan.ticketLink' : 'plan.fromLink')} ↗
                    </a>
                  </>
                )}
                {!flown && out.from && out.to && (out.from.own || out.to.own) && (
                  // Where it really leaves and arrives, when not the towns themselves.
                  <span className="quiet stop-ends">
                    {out.from.name} → {out.to.name}
                  </span>
                )}
                {out.fuel && tickets.length === 0 && (
                  <span className="quiet stop-fuel">
                    {t(plan.car.electric ? 'plan.charge' : 'plan.fuel', {
                      km: out.fuel.km.toLocaleString(lang),
                      litres: (out.fuel.tenths / 10).toLocaleString(lang, {
                        maximumFractionDigits: 1,
                      }),
                      price: formatMoney(out.fuel.perLitre, lang),
                    })}
                  </span>
                )}
              </span>
              <span className="stop-way-actions">
                {bookingActions(tickets[0])}
                {/* No rental to book for the traveller's own car. */}
                {onTicket && ticketWay && (
                  <button
                    type="button"
                    className="icon-button icon-button-small"
                    aria-label={t(ticketWord(ticketWay))}
                    title={t(ticketWord(ticketWay))}
                    onClick={() => {
                      onTicket(ticketWay);
                    }}
                  >
                    <TicketPlusIcon />
                  </button>
                )}
                {onEnds && out.from && out.to && (
                  <EndsPick
                    from={out.from}
                    to={out.to}
                    near={{ from: out.from, to: out.to }}
                    onSave={onEnds}
                  />
                )}
                {pick && tickets.length === 0 && !journeyStart && (
                  <WayPick
                    label={t('plan.changeWayMode')}
                    ways={stop.ways}
                    mode={out.mode}
                    onPick={pick}
                  />
                )}
                {onFuel && out.mode === 'car' && tickets.length === 0 && (
                  <button type="button" className="link-button row-action" onClick={onFuel}>
                    {t('plan.fuelEdit')}
                  </button>
                )}
              </span>
            </span>
            <span
              role="cell"
              className={
                tickets.length === 0 && journeyStart ? 'stop-priced-at' : 'mono strong'
              }
            >
              {tickets.length > 0
                ? money({ currency, minor: ticketTotal })
                : journeyStart
                  ? // Its price is the journey's, at its start; here only its times and link.
                    priced(
                      t('plan.pricedAt', { city: journeyStart.city }),
                      t('plan.changeWay', { way }),
                      [],
                      (values) =>
                        wayOnChange({ ...values, ranges: { estimate: cents(out.estimate) } }),
                      { times: clockOn, link: out.link },
                    )
                  : priced(
                      formatRange(out.estimate, lang),
                      t('plan.changeWay', { way }),
                      [
                        {
                          key: 'estimate',
                          label: t('stopForm.estimate'),
                          value: priceOffered(stop, out),
                        },
                      ],
                      drafts ? ticketChange : wayOnChange,
                      {
                        times: clockOn,
                        link: out.link,
                        ...(drafts
                          ? {
                              name: {
                                label: t('plan.ticket'),
                                value: drafts.ticket ? drafts.ticket.name : null,
                              },
                            }
                          : {}),
                      },
                    )}
            </span>
            {/* A way is spent on the day it goes, not shared over the days. */}
            <span role="cell" />
            <span role="cell">
              <StatusIcon status={tickets.length > 0 ? 'booked' : 'estimate'} />
            </span>
          </div>
        )}
      </div>

      <div className="stop-detail-foot">
        <p className="quiet">
          {t('plan.stopSums', {
            city: stop.city,
            paid: money(stop.cost.paid),
            toPay: money(stop.cost.toPay),
            estimate: formatRange(stop.cost.estimate, lang),
          })}
        </p>
        {stop.perDay && (
          <p className="stop-per-day">
            <span className="quiet">{t('plan.perDayLabel')}</span>
            <span className="mono">{formatRange(stop.perDay, lang, true)}</span>
          </p>
        )}
      </div>
      {viewing && (
        <FileView
          trip={plan.trip.id}
          file={{ id: viewing.id, name: viewing.name, type: viewing.file?.type }}
          onClose={() => {
            setViewing(null);
          }}
        />
      )}
    </section>
  );
}
